import { Dispatch, SetStateAction, FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import useDC from '@eneco/dc/useDC';
import { FlowError, StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import {
  Bleed,
  Carousel,
  RadioGroup,
  Stack,
  Divider,
  Box,
  Grid,
  RadioTile,
  VisuallyHidden,
  TextLink,
} from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { usePlaceholderContent } from '@sitecore/common';
import { Fields as OfferStepFieldsType } from '@sitecore/types/OfferStep';
import { OxxioOfferStepRendering, Fields } from '@sitecore/types/OxxioOfferStep';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { OverviewConsumption, OfferSection, Ribbon } from '../../../shared/components';
import {
  getOffers,
  getOffer,
  constructTextualDataKey,
  getOfferSubTitle,
  getCashbackTexts,
  getUpsellProductAvailability,
  checkAvailabilityOfferType,
  selectDefaultDiscountCode,
  checkOffersIncludesDynamicPricingProduct,
} from '../../../shared/helpers';
import {
  checkOfferIncludesDynamicOrVariablePricingProduct,
  checkOfferTypes,
  filterOffersByType,
  getAvailableOfferTypes,
  sortContractOptionsByPreferredOrder,
} from '../../../shared/helpers/misc';
import { checkOnDiscountType, getOfferTitle } from '../../../shared/helpers/sitecore';
import {
  useConsumptionOverview,
  useFlowTracking,
  useProductItemContent,
  useFlowNavigation,
  useCachedOfferMachine,
} from '../../../shared/hooks';
import { Offer, EnergyType, OfferType } from '../../../shared/types';
import { PriceDetailsOverviewOxxio } from '../../components';
import { ProductCard } from '../../components/ProductCard/ProductCardOxxio';

interface FormFields {
  discountCode?: string;
}

interface ContractOption {
  name: string;
  label: string;
  description?: string;
}

const OfferCard: FC<
  PropsWithChildren<{
    offer: Offer;
    isSME?: boolean;
    energyType?: EnergyType;
    textualData?: Fields;
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    isCashbackAvailable: boolean;
    isTagAvailable: boolean;
    productFilter?: string;
  }>
> = ({
  offer,
  isOpen,
  setOpen,
  isSME,
  isCashbackAvailable,
  isTagAvailable,
  energyType = EnergyType.Electricity,
  textualData,
  productFilter,
  children,
}) => {
  const { currency, format } = useFormatter();

  const textualDataKey = checkOnDiscountType(
    constructTextualDataKey(offer.contractSpan, energyType),
    offer?.discountType,
  );

  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);

  const productDetails = getProductDetails(textualDataKey);

  // Ensures that any product item is not rendered unless it is mapped on Sitecore, even though that's exposed by the api
  if (!productDetails) {
    return null;
  }

  const { promotionText, usps } = productDetails;

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  const cashbackTexts = getCashbackTexts(offer.costDiscounts?.cashback?.type, textualData?.promotionTexts);

  return (
    <ProductCard
      isOpen={isOpen}
      setOpen={setOpen}
      id={offer.type ?? ''}
      title={getOfferTitle(offer, textualData?.productData, textualData?.productTypeLabels)}
      subtitle={getOfferSubTitle(
        energyType,
        textualData?.productTypeLabels,
        offer?.discountType,
        textualData?.productData?.dynamicProductNameText?.value.toLowerCase(),
        textualData?.productData?.fixedPeriodLabel?.value.toLowerCase(),
      )}
      tag={offer.mostPopularChoice ? promotionText : productDetails?.tagLabel || undefined}
      isTagAvailable={isTagAvailable}
      usps={usps}
      priceMonthly={{
        value: offer?.costsMonthly?.[costsType] ?? 0,
        period:
          productFilter !== OfferType.Dynamic
            ? textualData?.productData?.perMonthLabel?.value
            : textualData?.productData?.perMonthEstimatedLabel?.value,
      }}
      priceYearly={{
        value: Number(offer?.costsYearly?.[costsType] ?? 0) - Number(offer?.costDiscounts?.cashback?.value ?? 0),
        previousValue:
          offer.costsYearlyPromotion || offer?.costDiscounts?.cashback?.value
            ? currency.euro(offer?.costsYearly?.[costsType] ?? 0)
            : undefined,
        period: textualData?.productData?.perYearLabel?.value,
      }}
      priceDetailsTriggerLabel={textualData?.productData?.tariffsTriggerText?.value}
      loyaltyBonusLabel={textualData?.productData?.loyaltyBonusLabel?.value}
      isCashbackAvailable={isCashbackAvailable}
      ribbon={
        cashbackTexts && (
          <Ribbon
            theme="oxxio"
            size="small"
            emphasis="medium"
            title={format(cashbackTexts.title, {
              [`${offer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
                offer?.costDiscounts?.cashback?.value ?? 0,
              ),
            })}
            text={cashbackTexts.text}
            trigger={cashbackTexts?.trigger}
          />
        )
      }>
      {children}
    </ProductCard>
  );
};

export const OfferStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const sendToFlowMachine = useFlowActorRef().send;
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const { OxxioOfferStep: { fields: textualData } = {} } = usePlaceholderContent<{
    OxxioOfferStep: OxxioOfferStepRendering;
  }>();
  // Pre-select 'Vast' if present, otherwise first available option
  const contractOptions: ContractOption[] = textualData?.productData?.contractTypeRadio?.value?.options ?? [];
  const sortedContractOptions = sortContractOptionsByPreferredOrder(contractOptions);
  const initialProductFilter =
    sortedContractOptions.find(option => option.label === 'Vast' || option.name === 'fixed')?.name ||
    sortedContractOptions[0]?.name ||
    '';
  const [productFilter, setProductFilter] = useState(initialProductFilter);
  const [showProductToggle, setShowProductToggle] = useState(true);
  const [showCard, setShowCard] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const {
    context: flowContext,
    context: { discountCode, hasUpsellElectricity, hasUpsellGas, energyType, isSME, isUsageDetailsEditProhibited },
  } = flowState;

  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const offer = offerState?.context?.offer;
  const availableOffers = getOffers(flowContext, offer);

  const { consumptionTextualData, consumptionData, consumptionEditButton } = useConsumptionOverview({
    flowContext,
    textualData: textualData as unknown as OfferStepFieldsType,
    sendToFlowMachine,
  });

  const offers = useMemo(() => {
    if (!showProductToggle) {
      return availableOffers;
    }

    if (!productFilter) return [];

    if (productFilter) {
      return filterOffersByType(availableOffers, productFilter);
    }
    return availableOffers.filter(offer => offer.discountType?.includes(productFilter));
  }, [availableOffers, productFilter]);

  const toggleCard = () => {
    setShowCard(true);
  };

  useEffect(() => {
    if (showCard && cardRef.current) {
      const card = cardRef.current;
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      setShowCard(false);
    }
  }, [showCard]);

  const formSchema = yup.object({
    discountCode: yup.string().required(textualData?.content?.noOfferSelectedNotification?.value.content ?? ''),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: {
      // For this step we won't be using the "defaultValue" straight away since the earlier
      // selected "discountCode" might no longer be available due to flow context changes
    },
  });

  const { trackViewItem, trackSelectItem, trackAddToCart } = useFlowTracking();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);

  const [isInitialOfferSelection, setInitialOfferSelection] = useState(!discountCode);

  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);
  const [shouldShowMoreInfoDialog, setShouldShowMoreInfoDialog] = useState(false);

  const isMediumBreakpoint = useMediaQuery('md');
  const isLargeBreakpoint = useMediaQuery('lg');
  const isExtraLargeBreakpoint = useMediaQuery('xl');
  const isCashbackAvailable = !!availableOffers?.some(offer => offer.costDiscounts?.cashback);
  const isTagAvailable = !!availableOffers?.some(offer => offer.mostPopularChoice);

  const [costPeriod, setCostPeriod] = useState('costsMonthly');
  void costPeriod;
  const shouldShowCarousel = !(
    isLargeBreakpoint ||
    (!isLargeBreakpoint && availableOffers.length < 2) ||
    (isMediumBreakpoint && availableOffers.length < 3) ||
    availableOffers.length === 1
  );
  const shouldShowCarouselIndicators = shouldShowCarousel && (!isExtraLargeBreakpoint || availableOffers.length > 3);
  const shouldAlignCrouselItems = shouldShowCarousel && isExtraLargeBreakpoint && availableOffers.length === 3;

  const selectedDiscountCode = watch('discountCode');
  const selectedDiscountCodeIndex = offers?.findIndex(offer => offer.type === selectedDiscountCode) ?? 0;

  useEffect(() => {
    if (!offers.length || offers?.some(offer => offer.type === selectedDiscountCode)) return;
    setValue('discountCode', selectDefaultDiscountCode(offers));
  }, [offers, selectedDiscountCode, setValue]);

  useEffect(() => {
    sendToOfferMachine({
      type: flowContext?.isContractExtension ? 'GET_EXTENSION_OFFER' : 'GET_OFFER',
      flowContext: { ...flowContext },
    });
  }, []);

  useEffect(() => {
    if (selectedDiscountCode) {
      if (isInitialOfferSelection) setInitialOfferSelection(false);

      const selectedOffer = getOffer({ ...flowContext, discountCode: selectedDiscountCode }, offer);

      if (selectedOffer) trackSelectItem(selectedOffer, offer, isInitialOfferSelection);
    }
  }, [selectedDiscountCode]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const isGasUpsellProductAvailable = getUpsellProductAvailability(
        'gas',
        { ...flowContext, discountCode: selectedDiscountCode },
        offer,
      );
      const isElectricityUpsellProductAvailable = getUpsellProductAvailability(
        'electricity',
        { ...flowContext, discountCode: selectedDiscountCode },
        offer,
      );

      const selectedOffer = getOffer({ ...flowContext, discountCode: selectedDiscountCode }, offer);

      if (selectedOffer) trackAddToCart(selectedOffer, offer);

      updateContextAndGoToNextStep({
        ...getValues(),
        isGasUpsellProductAvailable,
        isElectricityUpsellProductAvailable,
        hasUpsellGas: hasUpsellGas && isGasUpsellProductAvailable,
        hasUpsellElectricity: hasUpsellElectricity && isElectricityUpsellProductAvailable,
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING') || offerState.matches('GET_EXTENSION'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
      setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));
    }

    if (offerState.matches('SUCCESS')) {
      const selectedOfferType = checkAvailabilityOfferType(availableOffers, discountCode);
      const availableOfferTypes = getAvailableOfferTypes(availableOffers);
      setShowProductToggle(availableOfferTypes.length !== 1);

      if (selectedOfferType) {
        const isDynamicPricingProduct = checkOfferIncludesDynamicOrVariablePricingProduct(
          availableOffers.find(offer => offer.type === selectedOfferType),
        );
        setValue('discountCode', selectedOfferType);
        if (isDynamicPricingProduct) setProductFilter(isDynamicPricingProduct);
      }

      setShouldShowMoreInfoDialog(
        !!(
          checkOffersIncludesDynamicPricingProduct(availableOffers) &&
          textualData?.content?.moreInfoDialog?.value?.triggerText
        ),
      );

      availableOffers.forEach(availableOffer => {
        trackViewItem(availableOffer, offer);
      });
    }
  }, [offerState]);

  const handleValueChange = (value: string) => {
    setProductFilter(value);
    toggleCard();
  };

  const filteredOptions = checkOfferTypes(sortedContractOptions, availableOffers);
  const hasSubtitleLayout = filteredOptions[0]?.description;

  const gridLayout = hasSubtitleLayout ? '1fr' : { initial: '1fr', md: '1fr 1fr' };
  const getButtonLayout = () => {
    if (hasSubtitleLayout) return { md: `repeat(${contractOptions.length}, 1fr)` };

    return contractOptions.length > 2 ? '1fr' : '1fr 1fr';
  };

  const buttonLayout = getButtonLayout();

  const renderOfferCard = (offer: Offer) => {
    return (
      <OfferCard
        key={offer.type}
        isOpen={isPriceDetailsOpen}
        setOpen={setIsPriceDetailsOpen}
        offer={offer}
        textualData={textualData}
        isSME={isSME}
        energyType={energyType}
        isCashbackAvailable={isCashbackAvailable}
        productFilter={productFilter}
        isTagAvailable={isTagAvailable}>
        <OfferSection
          offer={offer}
          textualData={textualData}
          flowContext={flowContext}
          textualDataKey={checkOnDiscountType(
            constructTextualDataKey(offer?.contractSpan, energyType),
            offer?.discountType,
          )}
          setCostPeriod={setCostPeriod}
          showLegalText={false}>
          <PriceDetailsOverviewOxxio flowContext={flowContext} textualData={textualData} offer={offer} />
        </OfferSection>
      </OfferCard>
    );
  };

  const getErrorNotification = () => {
    if (isNotAuthenticated) return textualData?.genericError?.errorNotAuthenticatedNotification;
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;

    return textualData?.genericError?.errorNotification;
  };

  const getErrorButtonLabel = () => {
    if (isNotAuthenticated) return textualData?.genericError?.loginButtonText;
    if (!isNotAvailable && !isNotFound) return textualData?.genericError?.tryAgainButtonText;

    return undefined;
  };

  const renderOffers = () => {
    if (shouldShowCarousel) {
      const bleedRightWithoutAlignment = !isLargeBreakpoint ? 6 : 0;
      const bleedRight = shouldAlignCrouselItems ? 24 : bleedRightWithoutAlignment;

      return (
        <Bleed horizontal={!isLargeBreakpoint ? { initial: 6, md: 10 } : undefined} right={bleedRight}>
          <Carousel
            key={selectedDiscountCode}
            showIndicator={shouldShowCarouselIndicators}
            scrollBehavior="snap"
            scrollMarginX="gridGutter"
            hasCardPadding
            layout="default"
            startAt={selectedDiscountCodeIndex}
            scrollTo={selectedDiscountCodeIndex}>
            {offers.map(offer => (
              <Carousel.Item key={offer.type}>
                <Box ref={cardRef}>{renderOfferCard(offer)}</Box>
              </Carousel.Item>
            ))}
          </Carousel>
        </Bleed>
      );
    }

    if (offers.length === 1 && !isMediumBreakpoint) return offers.map(offer => renderOfferCard(offer));

    return (
      <Grid gridTemplateColumns="1fr 1fr 1fr" gap="6">
        {offers.map(offer => renderOfferCard(offer))}
      </Grid>
    );
  };

  return (
    <Layout.Content variant="C" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} align="center" />
          <Stack.Item>
            <Bleed horizontal="gridGutter">
              <Divider />
            </Bleed>

            <OverviewConsumption
              theme="oxxio"
              textualData={consumptionTextualData}
              data={consumptionData}
              editButton={isUsageDetailsEditProhibited ? undefined : consumptionEditButton}
            />

            {shouldShowMoreInfoDialog && (
              <TrackedDialog
                title={textualData?.content?.moreInfoDialog?.value?.title}
                trigger={
                  <TextLink emphasis="high">{textualData?.content?.moreInfoDialog?.value?.triggerText}</TextLink>
                }>
                <RichText html={textualData?.content?.moreInfoDialog?.value?.content} />
              </TrackedDialog>
            )}
            {showProductToggle && (
              <Box paddingTop="10" paddingBottom="5">
                <Grid gridTemplateColumns={gridLayout} gap="6">
                  <VisuallyHidden id="productFilter">
                    {textualData?.productData?.contractTypeRadio?.value?.label}
                  </VisuallyHidden>
                  <RadioGroup
                    aria-labelledby="productFilter"
                    direction={hasSubtitleLayout || filteredOptions.length > 2 ? 'column' : 'row'}
                    name="Productfilter"
                    value={productFilter}
                    onValueChange={e => handleValueChange(e)}
                    wrap={!hasSubtitleLayout}>
                    <Grid gridTemplateColumns={buttonLayout} gap="3">
                      {filteredOptions?.map(option => (
                        <RadioTile key={option.name} value={option.name} subtitle={option?.description}>
                          {option.label}
                        </RadioTile>
                      ))}
                    </Grid>
                  </RadioGroup>
                </Grid>
              </Box>
            )}
          </Stack.Item>
          {offers.length > 0 && (
            <>
              <Controller
                control={control}
                name="discountCode"
                render={({ field: { onChange, value, name } }) => (
                  <RadioGroup
                    aria-labelledby="discountCode"
                    name={name}
                    direction={availableOffers.length >= 2 ? { initial: 'column', md: 'row' } : 'row'}
                    alignY="start"
                    wrap={false}
                    value={value}
                    onValueChange={onChange}
                    error={errors['discountCode']?.message}>
                    {renderOffers()}
                  </RadioGroup>
                )}
              />

              <NextButton isLoading={isSubmitSuccessful} alignX={{ initial: 'justify', md: 'center' }}>
                {textualData?.content?.nextStepText?.value}
              </NextButton>
            </>
          )}
        </>
      )}

      {isFailed && (
        <Stack direction="column" gap="10">
          <FlowError
            alignY="center"
            type={isNotAuthenticated ? 'AUTHENTIFICATION' : 'GENERIC'}
            textualData={{
              notification: getErrorNotification(),
              buttonLabel: getErrorButtonLabel(),
            }}
          />
        </Stack>
      )}
    </Layout.Content>
  );
};
